import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@isophi/core-legacy';
import { BaseComponent, ToastService } from '@isophi/core-ui';
import {
  AnnouncementsService,
  Archive,
  ChildrenService,
  ClassesService,
  DayOffsService,
  DayOffTypeEnum,
  GradeBookService,
  InvitationsService,
  InviteSingleCloseRelative,
  PatchedChildGroupCreateOrUpdate,
  TeachersService,
} from '@isophi/mng-api';
import { PopUpComponent } from '@isophi/teachers-shared';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize, takeUntil } from 'rxjs';

import { MenuTypeEnum } from '../../../core/enums/menu-type.enum';
import { RouterLinkFactory } from '../../../core/router/router-link.factory';
import { HelperService } from '../../../core/services/helper.service';
import { UserService } from '../../../core/services/user.service';
import { EditAbsenceModalComponent } from '../../../modules/evidence/components/edit-absence-modal/edit-absence-modal.component';
import { normalizeUserRole, UserRoleType } from '../../../modules/user/constants/teacher-roles.constants';
import { HandleErrorService } from '../../services/handle-error.service';
import { TransferChildrenModalComponent } from '../transfer-children-modal/transfer-children-modal.component';
import { TransferWorkplaceModalComponent } from '../transfer-workplace-modal/transfer-workplace-modal.component';

@Component({
  selector: 'app-context-menu',
  templateUrl: './context-menu.component.html',
  styleUrls: ['./context-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContextMenuComponent extends BaseComponent {
  private readonly helperService = inject(HelperService);

  @Input() menuType: MenuTypeEnum;

  @Input()
  icon = 'more_horiz';

  // Types: ChildProfileList || ChildProfileDetail || ChildGroupDetail || CloseRelativeList || GradeBookRecordRead || AnnouncementRead || User || ChildAttendanceOverview || CurrentChildAbsence || KindergartenDayOffRead
  @Input() data;

  @Input()
  childId: string;

  @Input()
  backgroundColor: string;

  @Input() dateFrom?: string;

  @Input() dateTo?: string;

  @Output() refreshData? = new EventEmitter<void>();

  menuTypeEnum = MenuTypeEnum;

  dayOffTypeEnum = DayOffTypeEnum;

  constructor(
    public links: RouterLinkFactory,
    private childrenService: ChildrenService,
    private classesService: ClassesService,
    private gradeBookService: GradeBookService,
    private spinner: NgxSpinnerService,
    private translocoService: TranslocoService,
    private toastr: ToastService,
    private modal: NgbModal,
    private popUp: PopUpComponent,
    private authService: AuthService,
    private router: Router,
    private announcementService: AnnouncementsService,
    private userService: UserService,
    private teachersService: TeachersService,
    private invitationsService: InvitationsService,
    private handleErrorService: HandleErrorService,
    private dayOffsService: DayOffsService
  ) {
    super();
  }

  protected toggleArchiveEntity(event: Event, id: string, archive: boolean): void {
    event.stopPropagation();

    if (this.menuType == this.menuTypeEnum.CHILD) {
      this.toggleArchiveChild(id, archive);
    } else if (this.menuType == this.menuTypeEnum.GROUP) {
      this.toggleArchiveGroup(id, archive);
    }
  }

  private toggleArchiveChild(id: string, archive: boolean): void {
    this.spinner.show();
    const archiveChild: Archive = {
      childUuids: [id],
    };

    const apiCall$ = archive
      ? this.childrenService.childrenBulkArchiveCreate(archiveChild)
      : this.childrenService.childrenBulkArchiveRestoreCreate(archiveChild);

    apiCall$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: () => {
        this.toastr.success(
          archive
            ? this.translocoService.translate('child/archiveSuccessToast')
            : this.translocoService.translate('child/unarchiveSuccessToast')
        );
        this.refreshData.emit();
      },
      error: () => {
        this.toastr.error(
          archive
            ? this.translocoService.translate('child/archiveErrorToast')
            : this.translocoService.translate('child/unarchiveErrorToast')
        );
        this.spinner.hide();
      },
      complete: () => {
        this.spinner.hide();
      },
    });
  }

  private toggleArchiveGroup(id: string, archive: boolean): void {
    this.spinner.show();
    const archiveGroup: PatchedChildGroupCreateOrUpdate = {
      isArchived: archive,
    };

    this.classesService
      .classesPartialUpdate(id, archiveGroup)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: () => {
          this.toastr.success(
            archive
              ? this.translocoService.translate('group/archiveSuccessToast')
              : this.translocoService.translate('group/unarchiveSuccessToast')
          );
          this.refreshData.emit();
        },
        error: () => {
          this.toastr.error(
            archive
              ? this.translocoService.translate('group/archiveErrorToast')
              : this.translocoService.translate('group/unarchiveErrorToast')
          );
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  navToEdit(): string[] {
    if (this.menuType === this.menuTypeEnum.CHILD) {
      return this.links.editChild(this.data?.id || this.data?.childId);
    } else if (this.menuType === this.menuTypeEnum.GROUP) {
      return this.links.editGroup(this.data?.uuid);
    } else if (this.menuType === this.menuTypeEnum.CLOSE_RELATIVE) {
      return this.links.editCloseRelative(this.childId, this.data?.id);
    } else if (this.menuType === this.menuTypeEnum.GRADEBOOK) {
      return this.links.editGradebook(this.data?.id);
    } else if (this.menuType === this.menuTypeEnum.ANNOUNCEMENT) {
      return this.links.editAnnouncement(this.data?.id);
    } else if (this.menuType === this.menuTypeEnum.USER) {
      if (this.data?.teacherId) {
        return this.links.editUser(`${this.data?.teacherId}`);
      }
      return this.links.editUser(`${this.data?.teacher?.id}`);
    } else if (this.menuType === this.menuTypeEnum.DAY_OFF) {
      return this.links.editHolidaysDates(this.data?.id);
    }
  }

  openChildHistory(event: Event): void {
    event.stopPropagation();

    if (this.menuType !== this.menuTypeEnum.CHILD || !this.canShowChildHistory()) {
      return;
    }

    const childId = this.data?.id || this.data?.childId;

    if (!childId) {
      return;
    }

    this.router.navigate(this.links.childProfileHistory(childId));
  }

  canShowChildHistory(): boolean {
    return this.menuType === this.menuTypeEnum.CHILD;
  }

  canTransferWorkplace(): boolean {
    const teacher = this.authService.loggedUser?.teacher;
    const hasAccess = !!teacher?.isDirector?.();
    const hasMultipleWorkplaces = (teacher?.kindergartens || []).length > 1;
    return hasAccess && hasMultipleWorkplaces;
  }

  canTransferUserWorkplace(): boolean {
    if (!this.canTransferWorkplace() || this.menuType !== this.menuTypeEnum.USER) {
      return false;
    }

    return normalizeUserRole(this.data?.role) !== UserRoleType.DIRECTOR;
  }

  transferGroupToWorkplace(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.menuType !== this.menuTypeEnum.GROUP || !this.data?.uuid) {
      return;
    }
    const modalRef = this.modal.open(TransferWorkplaceModalComponent, { size: 'md', centered: true });
    modalRef.result
      .then((result) => {
        if (!result?.workplaceUuid) {
          return;
        }
        this.spinner.show();
        this.classesService
          .classesTransferToWorkplaceCreate({ workplaceUuid: result.workplaceUuid }, this.data.uuid)
          .pipe(
            takeUntil(this.ngUnsubscribe$),
            finalize(() => this.spinner.hide())
          )
          .subscribe({
            next: (response) => {
              this.toastr.success(this.translocoService.translate('transfer/classMovedSuccess', { workplace: response?.workplaceName }));
              this.refreshData.emit();
            },
            error: () => {
              this.toastr.error(this.translocoService.translate('transfer/classMovedError'));
            },
          });
      })
      .catch(() => null);
  }

  transferUserToWorkplace(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.menuType !== this.menuTypeEnum.USER) {
      return;
    }
    const userId = this.data?.teacher?.id ? this.data?.teacher?.id : this.data?.teacherId;
    if (!userId) {
      return;
    }
    const currentWorkplaceUuid = this.userService.getCurrentKindergartenUUID();
    const modalRef = this.modal.open(TransferWorkplaceModalComponent, { size: 'md', centered: true });
    modalRef.componentInstance.titleKey = 'transfer/changeWorkplaceTitle';
    modalRef.componentInstance.labelKey = 'transfer/moveUserToWorkplace';
    modalRef.componentInstance.currentWorkplaceUuid = currentWorkplaceUuid;
    modalRef.result
      .then((result) => {
        if (!result?.workplaceUuid || (currentWorkplaceUuid && result.workplaceUuid === currentWorkplaceUuid)) {
          return;
        }
        this.spinner.show();
        this.teachersService
          .teachersTransferToWorkplaceCreate({ workplaceUuid: result.workplaceUuid }, userId)
          .pipe(finalize(() => this.spinner.hide()))
          .subscribe({
            next: (response) => {
              this.toastr.success(this.translocoService.translate('transfer/userMovedSuccess', { workplace: response?.workplaceName }));
              this.refreshData.emit();
            },
            error: () => {
              this.toastr.error(this.translocoService.translate('transfer/userMovedError'));
            },
          });
      })
      .catch(() => null);
  }

  transferChildToWorkplace(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const childUuid = this.data?.id || this.data?.childId || this.data?.uuid;
    if (this.menuType !== this.menuTypeEnum.CHILD || !childUuid) {
      return;
    }
    const modalRef = this.modal.open(TransferChildrenModalComponent, { size: 'md', centered: true });
    modalRef.componentInstance.selectedChildrenCount = 1;
    modalRef.componentInstance.currentWorkplaceUuid = this.authService.loggedUser?.teacher?.kindergartens?.[0]?.uuid || null;

    modalRef.result
      .then((result) => {
        if (!result?.workplaceUuid) {
          return;
        }
        const payload = {
          childUuids: [childUuid],
          workplaceUuid: result.workplaceUuid,
          classUuid: result.classUuid ?? null,
        };

        this.spinner.show();
        this.childrenService
          .childrenTransferToWorkplaceCreate(payload)
          .pipe(finalize(() => this.spinner.hide()))
          .subscribe({
            next: (response) => {
              this.toastr.success(this.translocoService.translate('transfer/childrenMovedSuccess', { workplace: response?.workplaceName }));
              this.refreshData.emit();
            },
            error: () => {
              this.toastr.error(this.translocoService.translate('transfer/childrenMovedError'));
            },
          });
      })
      .catch(() => null);
  }

  openModal(): void {
    const modalRef = this.modal.open(EditAbsenceModalComponent, { size: 'md', centered: true });
    modalRef.componentInstance.childAttendanceOverview = this.data;
    modalRef.componentInstance.dateFrom = this.helperService.parseDate(this.dateFrom);
    modalRef.componentInstance.dateTo = this.helperService.parseDate(this.dateTo);
    modalRef.componentInstance.title = 'evidence/editAttendance2';
  }

  async removeEntity(event: Event, id: string): Promise<void> {
    event.stopPropagation();

    if (this.menuType === this.menuTypeEnum.ANNOUNCEMENT) {
      await this.removeAnnouncement(id);
    } else if (this.menuType === this.menuTypeEnum.GRADEBOOK) {
      await this.removeGradebook(id);
    }
  }
  async removeGradebook(id: string): Promise<void> {
    const title = this.translocoService.translate('gradebook/remove');
    const text = this.translocoService.translate('shared/confirm');
    const yesText = this.translocoService.translate('shared/YES');
    const noText = this.translocoService.translate('shared/NO');
    const confirm = await this.popUp.confirm(title, text, null, yesText, noText, 'btn-primary', 'btn-outline-primary');

    if (!confirm) return;

    await this.spinner.show();

    this.gradeBookService
      .gradeBookDestroy(id)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: () => {
          this.toastr.success(this.translocoService.translate('gradebook/removeSuccessToast'));
          this.refreshData.emit();
        },
        error: () => {
          this.toastr.error(this.translocoService.translate('gradebook/removeErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  async removeAnnouncement(id: string): Promise<void> {
    const title = this.translocoService.translate('announcement/removeAnnouncement');
    const text = this.translocoService.translate('shared/confirm');
    const yesText = this.translocoService.translate('shared/YES');
    const noText = this.translocoService.translate('shared/NO');
    const confirm = await this.popUp.confirm(title, text, null, yesText, noText, 'btn-primary', 'btn-outline-primary');

    if (!confirm) return;

    await this.spinner.show();
    this.announcementService
      .announcementsDestroy(id)
      .pipe(
        finalize(() => this.spinner.hide()),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translocoService.translate('announcement/removeAnnouncementSuccess'),
            this.translocoService.translate('announcement/removeAnnouncement')
          );
          this.refreshData.emit();
        },
        error: (e) => {
          this.handleErrorService.handleFormError(e, 'announcement/removeAnnouncement', 'announcement/removeAnnouncementFailed');
        },
      });
  }

  async removeUser(e): Promise<void> {
    e.stopPropagation();

    const title = this.translocoService.translate('user/remove');
    const text = this.translocoService.translate('shared/confirm');
    const yesText = this.translocoService.translate('shared/YES');
    const noText = this.translocoService.translate('shared/NO');
    const confirm = await this.popUp.confirm(title, text, null, yesText, noText, 'btn-primary', 'btn-outline-primary');

    if (!confirm) return;

    await this.spinner.show();
    this.userService
      .removeUser(this.data?.teacher?.id ? this.data?.teacher?.id : this.data.teacherId)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translocoService.translate('user/userRemoveSuccessToast'),
            this.translocoService.translate('user/remove')
          );
          this.refreshData.emit();
        },
        error: (e) => {
          this.handleErrorService.handleFormError(e, 'user/remove', 'user/userRemoveErrorToast');
        },
      });
  }

  inviteParent(): void {
    this.spinner.show();

    const data: InviteSingleCloseRelative = {
      childUuid: this.childId,
      closeRelativeId: this.data?.id,
    };

    this.invitationsService
      .invitationsInviteSingleCloseRelativeCreate(data)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translocoService.translate('child/inviteParentSuccess'),
            this.translocoService.translate('child/inviteParent')
          );
          this.refreshData.emit();
        },
        error: (e) => {
          this.handleErrorService.handleFormError(e, 'child/inviteParent', 'child/inviteParentError');
        },
      });
  }

  async removeAbsence(): Promise<void> {
    const title = this.translocoService.translate('child/removeAbsence');
    const text = this.translocoService.translate('shared/confirm');
    const yesText = this.translocoService.translate('shared/YES');
    const noText = this.translocoService.translate('shared/NO');
    const confirm = await this.popUp.confirm(title, text, null, yesText, noText, 'btn-primary', 'btn-outline-primary');

    if (!confirm) return;

    await this.spinner.show();
    this.childrenService
      .childrenAbsenceDestroy(this.data?.id, this.childId)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translocoService.translate('child/removeAbsenceSuccess'),
            this.translocoService.translate('child/removeAbsence')
          );
          this.refreshData.emit();
        },
        error: (e) => {
          this.handleErrorService.handleFormError(e, 'child/removeAbsence', 'child/removeAbsenceFailed');
        },
      });
  }

  async removeDayOff(): Promise<void> {
    const title = this.translocoService.translate('settings/removeDayOff');
    const text = this.translocoService.translate('shared/confirm');
    const yesText = this.translocoService.translate('shared/YES');
    const noText = this.translocoService.translate('shared/NO');
    const confirm = await this.popUp.confirm(title, text, null, yesText, noText, 'btn-primary', 'btn-outline-primary');

    if (!confirm) return;

    await this.spinner.show();
    this.dayOffsService
      .dayOffsDestroy(this.data?.id)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translocoService.translate('settings/removeDayOffSuccess'),
            this.translocoService.translate('settings/removeDayOff')
          );
          this.refreshData.emit();
        },
        error: (e) => {
          this.handleErrorService.handleFormError(e, 'settings/removeDayOff', 'settings/removeDayOffFailed');
        },
      });
  }
}
